import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFilePdf } from "react-icons/fa";
import { AppCard, AppButton, SectionLabel, IconBox } from "./app";
import { appColors, appType } from "../utils/appTheme";
import { formatDateLocalized } from "../utils/formatters";
import { comunicacionResidente } from "../utils/comunicaciones/api";

/**
 * Comunicaciones de la administración que le mandaron al residente (PDF por
 * WhatsApp y email): quedan también acá, por si no las guardó. Arriba de las
 * novedades; si no hay ninguna (o el módulo no está habilitado), no se muestra.
 */
export default function ComunicacionesResidente({ buildingId }) {
  const { t } = useTranslation("novedades");
  const [lista, setLista] = useState([]);
  const [abriendo, setAbriendo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!buildingId) {
      setLista([]);
      return undefined;
    }
    let vivo = true;
    comunicacionResidente("listar", { buildingId })
      .then((r) => vivo && setLista((r && r.comunicaciones) || []))
      .catch(() => vivo && setLista([]));
    return () => {
      vivo = false;
    };
  }, [buildingId]);

  if (!lista.length) return null;

  const abrir = async (c) => {
    setAbriendo(c.id);
    setError("");
    // La pestaña se abre en el click (si se abre después del await, Safari la bloquea).
    const ventana = window.open("", "_blank");
    try {
      const { url } = await comunicacionResidente("pdf", { id: c.id });
      if (ventana) ventana.location.href = url;
      else window.location.href = url;
    } catch (_) {
      if (ventana) ventana.close();
      setError(t("comunicaciones.error"));
    } finally {
      setAbriendo(null);
    }
  };

  return (
    <section style={{ marginBottom: 20 }} aria-label={t("comunicaciones.title")}>
      <SectionLabel style={{ marginBottom: 12 }}>{t("comunicaciones.title")}</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {lista.map((c) => (
          <AppCard key={c.id} accent="blue" padding={16}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <IconBox icon={<FaFilePdf />} color={appColors.blue} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...appType.statLabel, color: appColors.blue, marginBottom: 3 }}>
                  {c.numero ? t("comunicaciones.numero", { numero: c.numero }) : t("comunicaciones.sinNumero")}
                  {c.enviadaEnMs ? ` · ${formatDateLocalized(new Date(c.enviadaEnMs), { month: "short" })}` : ""}
                </div>
                <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>{c.asunto || c.titulo}</div>
              </div>
              <AppButton variant="outline" onClick={() => abrir(c)} disabled={abriendo === c.id}>
                {t("comunicaciones.verPdf")}
              </AppButton>
            </div>
          </AppCard>
        ))}
      </div>
      {error && (
        <p role="alert" style={{ ...appType.body, color: appColors.ink2, margin: "8px 0 0" }}>
          {error}
        </p>
      )}
    </section>
  );
}
